import React, { useEffect } from 'react';
import styled from 'styled-components';
import { FaInstagram, FaTiktok, FaYoutube } from 'react-icons/fa';
import { PageContainer } from '../App';

const TikTokEmbed = () => {
  useEffect(() => {
    // Load TikTok embed script
    const script = document.createElement('script');
    script.src = 'https://www.tiktok.com/embed.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div>
      <blockquote 
        className="tiktok-embed" 
        cite="https://www.tiktok.com/@parentingbeyondpe" 
        data-unique-id="parentingbeyondpe" 
        data-embed-type="creator" 
        style={{ maxWidth: '780px', minWidth: '288px', margin: '0 auto' }}
      >
        <section>
          <a target="_blank" href="https://www.tiktok.com/@parentingbeyondpe?refer=creator_embed" rel="noopener noreferrer">
            @parentingbeyondpe
          </a>
        </section>
      </blockquote>
    </div>
  );
};

const AboutPage = () => {
  return (
    <PageContainer>
      <AboutContent>
        <SocialLinks>
          <SocialLink href="https://www.instagram.com/parentingbeyond_perfection" target="_blank" rel="noopener noreferrer">
            <FaInstagram size={24} />
            <SocialLabel>Instagram</SocialLabel>
          </SocialLink>
          <SocialLink href="https://www.tiktok.com/@parentingbeyondpe" target="_blank" rel="noopener noreferrer">
            <FaTiktok size={22} />
            <SocialLabel>TikTok</SocialLabel>
          </SocialLink>
          <SocialLink href="https://www.youtube.com/@ParentingBeyondPerfection" target="_blank" rel="noopener noreferrer">
            <FaYoutube size={24} />
            <SocialLabel>YouTube</SocialLabel>
          </SocialLink>
        </SocialLinks>

        <Divider />
        
        <AboutTitle>About Us</AboutTitle>
        
        <Section>
          <p>
            We're a family that believes parenting is an art—a balance of modern methods and timeless wisdom. 
            As passionate parents, we question the norm, embrace learning, and aim to create meaningful experiences 
            for our little one while inspiring others on their journey.
          </p>
        </Section>

        <Section>
          <p>
            For us, parenting isn't about blindly following traditions or keeping up with trends. It's about picking 
            the best from both worlds—modern research and the invaluable lessons passed down from our ancestors. 
            We avoided screen time for our daughter until she was 2, and even now, at 2.5, it's rare. Instead, 
            we focus on hands-on teaching, sparking curiosity, and exploring the world together, one day at a time.
          </p>
        </Section>

        <Section>
          <p>
            Our approach to parenting is built on balance. We support each other through the chaos, respecting 
            the need for personal time while ensuring our daughter always feels loved and nurtured. It's teamwork 
            at its finest—when one of us is busy, the other steps in without hesitation, creating a rhythm that 
            works for everyone.
          </p>
        </Section>

        <Section>
          <p>
            And then there's travel—our favorite classroom. We've explored 15+ countries with our daughter, 
            proving that parenting and adventure can go hand in hand. From packing smart to embracing unpredictability, 
            we've discovered tips and tricks that make traveling with kids not just possible but fun. Along the way, 
            other parents started asking us for advice, and that's when we realized—we have stories and insights worth sharing.
          </p>
        </Section>

        <Section>
          <p>
            This space is our way of giving back. It's where we share our journey, from fostering a love for learning 
            to navigating parenthood with purpose. Whether it's tackling mealtime struggles, mastering screen-free play, 
            or blending ancestral wisdom with modern parenting, we're here to inspire and help. Parenting might not come 
            with a guidebook, but with a little creativity, a lot of love, and shared experiences, it can become the most 
            rewarding adventure of all.
          </p>
        </Section>

        <EmbedSection>
          <EmbedTitle>Follow Us on TikTok</EmbedTitle>
          <TikTokEmbed />
        </EmbedSection>
      </AboutContent>
    </PageContainer>
  );
};

const AboutContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  font-family: 'Source Serif 4', Georgia, serif;
  color: #292929;
  line-height: 1.8;
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    gap: 2rem;
  }
`;

const SocialLink = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #292929;
  text-decoration: none;
  transition: transform 0.2s ease, color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    color: #4A90E2;
  }
`;

const SocialLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
`;

const Divider = styled.div`
  width: 80px;
  height: 2px;
  background: #4A90E2;
  margin: 0 auto 3rem;
`;

const AboutTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 700;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  
  p {
    font-size: 1.1rem;
    margin: 0;
  }
`;

const EmbedSection = styled.div`
  margin-top: 4rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const EmbedTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  font-weight: 600;
  color: #292929;
`;

export default AboutPage; 